import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import CloseIcon from "@material-ui/icons/Close";
import DateRangeIcon from "@material-ui/icons/DateRange";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import ReplayIcon from "@material-ui/icons/Replay";
import SearchIcon from "@material-ui/icons/Search";
import SaveIcon from "@material-ui/icons/Save";
import DescriptionIcon from "@material-ui/icons/Description";
import GetAppIcon from "@material-ui/icons/GetApp";

import Audio from "../../components/Audio";
import ModalImageCors from "../../components/ModalImageCors";
import usePaginatedUsers from "../../hooks/usePaginatedUsers";
import api from "../../services/api";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
    overflow: "hidden"
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    gap: theme.spacing(2),
    flexWrap: "wrap"
  },
  titleBlock: {
    minWidth: 240
  },
  pageTitle: {
    fontWeight: 700
  },
  summary: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  filters: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  quickFilters: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
  },
  board: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(240px, 1fr))",
    gap: theme.spacing(1.5),
    overflowX: "auto",
    height: "calc(100vh - 230px)",
    paddingBottom: theme.spacing(1)
  },
  column: {
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    minWidth: 240,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column"
  },
  columnHeader: {
    padding: theme.spacing(1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
  },
  columnBody: {
    padding: theme.spacing(1),
    overflowY: "auto"
  },
  ticketCard: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 5,
    cursor: "pointer",
    transition: "box-shadow 160ms ease, transform 160ms ease",
    "&:hover": {
      boxShadow: theme.shadows[2],
      transform: "translateY(-1px)"
    }
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  cardTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5)
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: 12
  },
  lastMessage: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: 13,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  cardActions: {
    display: "grid",
    gap: theme.spacing(1)
  },
  quickActions: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(0.75)
  },
  statusSelect: {
    minWidth: "100%"
  },
  empty: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: "center"
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingRight: theme.spacing(1)
  },
  dialogActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  ticketMeta: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr"
    }
  },
  metaItem: {
    padding: theme.spacing(1),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default
  },
  messagesBox: {
    height: "70vh",
    overflowY: "auto",
    background: theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5)
  },
  messageRow: {
    display: "flex",
    marginBottom: theme.spacing(1)
  },
  messageRowFromMe: {
    justifyContent: "flex-end"
  },
  messageBubble: {
    maxWidth: "78%",
    padding: theme.spacing(1),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word"
  },
  messageMedia: {
    marginTop: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75),
    maxWidth: 340,
    "& video": {
      maxWidth: "100%",
      maxHeight: 360,
      borderRadius: 6,
      display: "block"
    },
    "& audio": {
      maxWidth: "100%"
    }
  },
  filePreview: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: 6,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary
  },
  fileInfo: {
    minWidth: 0,
    flex: 1
  },
  fileName: {
    fontSize: 13,
    fontWeight: 600,
    overflowWrap: "anywhere"
  },
  fileType: {
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  messageBubbleFromMe: {
    background: theme.palette.type === "dark" ? "#1f3d35" : "#dcf8c6",
    color: theme.palette.text.primary
  },
  messageMeta: {
    display: "block",
    fontSize: 11,
    opacity: 0.75,
    marginBottom: theme.spacing(0.5)
  },
  loadMore: {
    marginBottom: theme.spacing(1)
  }
}));

const auditColumns = [
  { key: "pending", label: "Sem análise", color: "#90a4ae" },
  { key: "reviewing", label: "Em análise", color: "#1976d2" },
  { key: "issue", label: "Com pendência", color: "#f9a825" },
  { key: "approved", label: "Aprovado", color: "#2e7d32" },
  { key: "rejected", label: "Reprovado", color: "#c62828" }
];

const ticketStatuses = [
  { key: "", label: "Todos" },
  { key: "open", label: "Aberto" },
  { key: "pending", label: "Pendente" },
  { key: "closed", label: "Finalizado" }
];

const formatInputDate = date => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getTodayFilters = () => ({
  ...getCurrentMonthFilters(),
  startDate: formatInputDate(new Date()),
  endDate: formatInputDate(new Date())
});

const getLastMonthFilters = () => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endDate = new Date(now.getFullYear(), now.getMonth(), 0);

  return {
    ...getCurrentMonthFilters(),
    startDate: formatInputDate(startDate),
    endDate: formatInputDate(endDate)
  };
};

const getCurrentMonthFilters = () => {
  const now = new Date();
  const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
  const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    message: "",
    userId: "",
    queueId: "",
    tagId: "",
    whatsappId: "",
    ticketStatus: "",
    auditStatus: "",
    startDate: formatInputDate(startDate),
    endDate: formatInputDate(endDate),
    limit: "150"
  };
};

const getAudit = ticket => ticket.auditoria || {};

const getStatusInfo = status =>
  auditColumns.find(column => column.key === status) || auditColumns[0];

const formatDateTime = value => {
  if (!value) return "";
  return new Date(value).toLocaleString("pt-BR");
};

const getFileName = message => {
  const rawName = message.body || message.mediaUrl || "Arquivo";
  const cleanName = String(rawName).split("?")[0].split("/").pop();
  return cleanName || "Arquivo";
};

const TicketAudits = () => {
  const classes = useStyles();
  const history = useHistory();
  const [filters, setFilters] = useState(getCurrentMonthFilters);
  const [appliedFilters, setAppliedFilters] = useState(getCurrentMonthFilters);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingTicketId, setSavingTicketId] = useState(null);
  const {
    users,
    loading: loadingUsers,
    menuProps: usersMenuProps
  } = usePaginatedUsers();
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [tags, setTags] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [conversationOpen, setConversationOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesPage, setMessagesPage] = useState(1);
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);

  const loadFiltersData = useCallback(async () => {
    try {
      const { data } = await api.get("/queue");
      setQueues(Array.isArray(data) ? data : []);
    } catch (err) {
      setQueues([]);
    }

    try {
      const { data } = await api.get("/whatsapp");
      setWhatsapps(Array.isArray(data) ? data : []);
    } catch (err) {
      setWhatsapps([]);
    }

    try {
      const { data } = await api.get("/tags");
      setTags(Array.isArray(data) ? data : []);
    } catch (err) {
      setTags([]);
    }
  }, []);

  const loadAudits = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ticket-audits", {
        params: appliedFilters
      });
      setGrouped(data.grouped || {});

      const nextDrafts = {};
      Object.values(data.grouped || {})
        .flat()
        .forEach(ticket => {
          nextDrafts[ticket.id] = {
            status: ticket.auditStatus || "pending",
            observation: getAudit(ticket).observation || ""
          };
        });
      setDrafts(nextDrafts);
    } catch (err) {
      toast.error("Erro ao carregar auditoria de tickets.");
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadFiltersData();
  }, [loadFiltersData]);

  useEffect(() => {
    loadAudits();
  }, [loadAudits]);

  const handleFilterChange = event => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = event => {
    event.preventDefault();
    setAppliedFilters(filters);
  };

  const handleDraftChange = (ticketId, field, value) => {
    setDrafts(prev => ({
      ...prev,
      [ticketId]: {
        ...prev[ticketId],
        [field]: value
      }
    }));
  };

  const saveAudit = async (ticket, draftOverride) => {
    const draft = draftOverride || drafts[ticket.id] || {
      status: "pending",
      observation: ""
    };

    if (
      ["issue", "rejected"].includes(draft.status) &&
      !draft.observation?.trim()
    ) {
      toast.warning("Informe uma observação para pendência ou reprovação.");
      return;
    }

    setSavingTicketId(ticket.id);

    try {
      await api.put(`/ticket-audits/${ticket.id}`, draft);
      toast.success(
        `Ticket #${ticket.id} marcado como ${getStatusInfo(draft.status).label}.`
      );
      await loadAudits();
    } catch (err) {
      toast.error("Erro ao salvar auditoria.");
    } finally {
      setSavingTicketId(null);
    }
  };

  const loadMessages = useCallback(
    async ({ ticket, pageNumber = 1, prepend = false }) => {
      if (!ticket?.id) return;

      setMessagesLoading(true);
      try {
        const { data } = await api.get(
          `/ticket-audits/${ticket.id}/messages`,
          {
            params: { pageNumber }
          }
        );

        setMessages(prev =>
          prepend ? [...(data.messages || []), ...prev] : data.messages || []
        );
        setMessagesHasMore(Boolean(data.hasMore));
        setMessagesPage(pageNumber);
      } catch (err) {
        toast.error("Erro ao carregar conversa do ticket.");
      } finally {
        setMessagesLoading(false);
      }
    },
    []
  );

  const openConversation = ticket => {
    setSelectedTicket(ticket);
    setConversationOpen(true);
    setMessages([]);
    setMessagesPage(1);
    setMessagesHasMore(false);
    loadMessages({ ticket, pageNumber: 1 });
  };

  const closeConversation = () => {
    setConversationOpen(false);
    setSelectedTicket(null);
    setMessages([]);
  };

  const loadPreviousMessages = () => {
    if (!selectedTicket || messagesLoading) return;
    loadMessages({
      ticket: selectedTicket,
      pageNumber: messagesPage + 1,
      prepend: true
    });
  };

  const setPeriod = nextFilters => {
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      message: "",
      userId: "",
      queueId: "",
      tagId: "",
      whatsappId: "",
      ticketStatus: "",
      auditStatus: "",
      startDate: "",
      endDate: "",
      limit: "150"
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
  };

  const saveQuickStatus = (event, ticket, status) => {
    event.stopPropagation();
    const draft = {
      ...(drafts[ticket.id] || {
        status: ticket.auditStatus || "pending",
        observation: getAudit(ticket).observation || ""
      }),
      status
    };

    setDrafts(prev => ({ ...prev, [ticket.id]: draft }));
    saveAudit(ticket, draft);
  };

  const openTicketPage = () => {
    if (!selectedTicket?.id) return;
    closeConversation();
    history.push(`/tickets/${selectedTicket.id}`);
  };

  const renderMessageMedia = message => {
    if (!message.mediaUrl) return null;

    const mediaType = String(message.mediaType || "").toLowerCase();

    if (mediaType === "image" || /\.(jpe?g|png|gif|webp)$/i.test(message.mediaUrl)) {
      return (
        <div className={classes.messageMedia}>
          <ModalImageCors imageUrl={message.mediaUrl} />
        </div>
      );
    }

    if (mediaType === "video") {
      return (
        <div className={classes.messageMedia}>
          <video src={message.mediaUrl} controls />
        </div>
      );
    }

    if (mediaType === "audio" || mediaType === "ptt") {
      return (
        <div className={classes.messageMedia}>
          <Audio url={message.mediaUrl} />
        </div>
      );
    }

    return (
      <div className={classes.messageMedia}>
        <div className={classes.filePreview}>
          <DescriptionIcon color="action" />
          <div className={classes.fileInfo}>
            <div className={classes.fileName}>{getFileName(message)}</div>
            <div className={classes.fileType}>
              {message.mediaType || "arquivo"}
            </div>
          </div>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            startIcon={<GetAppIcon />}
            href={message.mediaUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir arquivo
          </Button>
        </div>
      </div>
    );
  };

  const renderMessageBody = message => {
    if (message.isInternal) {
      return <>Observação interna: {message.body || "Mensagem"}</>;
    }

    const hasMedia = Boolean(message.mediaUrl);
    const body = message.body;
    const shouldShowBody =
      body &&
      (!hasMedia || !String(body).startsWith("http"));

    return (
      <>
        {renderMessageMedia(message)}
        {shouldShowBody ? body : !hasMedia ? message.mediaType || "Mensagem" : null}
      </>
    );
  };

  const columns = useMemo(
    () =>
      auditColumns.map(column => ({
        ...column,
        tickets: grouped[column.key] || []
      })),
    [grouped]
  );

  const totalContacts = useMemo(
    () => columns.reduce((total, column) => total + column.tickets.length, 0),
    [columns]
  );

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <Typography variant="h5" className={classes.pageTitle}>
            Auditoria de contatos
          </Typography>
          <Typography className={classes.muted}>
            Revise conversas, registre observações e acompanhe pendências.
          </Typography>
        </div>
        <div className={classes.summary}>
          <Chip color="primary" label={`${totalContacts} contatos`} />
          <Chip
            icon={<DateRangeIcon />}
            label={
              appliedFilters.startDate && appliedFilters.endDate
                ? `${appliedFilters.startDate} até ${appliedFilters.endDate}`
                : "Todos os períodos"
            }
          />
        </div>
      </div>

      <Paper className={classes.filters} variant="outlined">
        <div className={classes.quickFilters}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPeriod(getTodayFilters())}
          >
            Hoje
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPeriod(getCurrentMonthFilters())}
          >
            Este mês
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setPeriod(getLastMonthFilters())}
          >
            Mês passado
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={clearFilters}
          >
            Limpar filtros
          </Button>
        </div>
        <form onSubmit={applyFilters}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Mensagem"
                name="message"
                value={filters.message}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Usuário</InputLabel>
                <Select
                  name="userId"
                  value={filters.userId}
                  onChange={handleFilterChange}
                  MenuProps={usersMenuProps}
                  label="Usuário"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                  {loadingUsers && (
                    <MenuItem disabled>
                      <CircularProgress size={18} />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Departamento</InputLabel>
                <Select
                  name="queueId"
                  value={filters.queueId}
                  onChange={handleFilterChange}
                  label="Departamento"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {queues.map(queue => (
                    <MenuItem key={queue.id} value={queue.id}>
                      {queue.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Etiqueta</InputLabel>
                <Select
                  name="tagId"
                  value={filters.tagId}
                  onChange={handleFilterChange}
                  label="Etiqueta"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {tags.map(tag => (
                    <MenuItem key={tag.id} value={tag.id} disabled={tag.isGroup}>
                      {tag.isGroup
                        ? tag.name
                        : `${tag.parent?.name ? `${tag.parent.name} / ` : ""}${tag.name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Instância</InputLabel>
                <Select
                  name="whatsappId"
                  value={filters.whatsappId}
                  onChange={handleFilterChange}
                  label="Instância"
                >
                  <MenuItem value="">Todas</MenuItem>
                  {whatsapps.map(whatsapp => (
                    <MenuItem key={whatsapp.id} value={whatsapp.id}>
                      {whatsapp.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status ticket</InputLabel>
                <Select
                  name="ticketStatus"
                  value={filters.ticketStatus}
                  onChange={handleFilterChange}
                  label="Status ticket"
                >
                  {ticketStatuses.map(status => (
                    <MenuItem key={status.key} value={status.key}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status auditoria</InputLabel>
                <Select
                  name="auditStatus"
                  value={filters.auditStatus}
                  onChange={handleFilterChange}
                  label="Status auditoria"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {auditColumns.map(status => (
                    <MenuItem key={status.key} value={status.key}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Limite</InputLabel>
                <Select
                  name="limit"
                  value={filters.limit}
                  onChange={handleFilterChange}
                  label="Limite"
                >
                  {[50, 100, 150, 300, 500].map(limit => (
                    <MenuItem key={limit} value={String(limit)}>
                      {limit}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="De"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                label="Até"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                variant="outlined"
                size="small"
                type="date"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                type="submit"
                color="primary"
                variant="contained"
                startIcon={<SearchIcon />}
                disabled={loading}
              >
                Filtrar
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {loading ? (
        <CircularProgress />
      ) : (
        <div className={classes.board}>
          {columns.map(column => (
            <section key={column.key} className={classes.column}>
              <div className={classes.columnHeader}>
                <Typography
                  variant="subtitle2"
                  style={{ color: column.color, fontWeight: 700 }}
                >
                  {column.label}
                </Typography>
                <Chip size="small" label={column.tickets.length} />
              </div>
              <div className={classes.columnBody}>
                {column.tickets.length === 0 && (
                  <div className={classes.empty}>Sem tickets</div>
                )}
                {column.tickets.map(ticket => {
                  const draft = drafts[ticket.id] || {
                    status: ticket.auditStatus || "pending",
                    observation: getAudit(ticket).observation || ""
                  };
                  const statusInfo = getStatusInfo(draft.status);

                  return (
                    <Paper
                      key={ticket.id}
                      className={classes.ticketCard}
                      variant="outlined"
                      style={{ borderLeftColor: statusInfo.color }}
                      onClick={() => openConversation(ticket)}
                    >
                      <div className={classes.cardHeader}>
                        <Typography className={classes.cardTitle}>
                          #{ticket.id} - {ticket.contact?.name || "Contato"}
                        </Typography>
                        <Tooltip title="Abrir conversa">
                          <IconButton
                            size="small"
                            onClick={event => {
                              event.stopPropagation();
                              openConversation(ticket);
                            }}
                          >
                            <ChatBubbleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                      <Typography className={classes.muted}>
                        {ticket.contact?.number || "-"} | Ticket: {ticket.status}
                      </Typography>
                      <Typography className={classes.muted}>
                        Usuário: {ticket.user?.name || "Sem usuário"}
                      </Typography>
                      <Typography className={classes.muted}>
                        Departamento: {ticket.queue?.name || "Sem departamento"}
                      </Typography>
                      <Typography className={classes.muted}>
                        Instância: {ticket.whatsapp?.name || "Sem instância"}
                      </Typography>
                      <Typography className={classes.muted}>
                        Etiqueta: {(ticket.tags || []).map(tag => tag.name).join(", ") || ticket.tag?.name || "Sem etiqueta"}
                      </Typography>
                      <Typography className={classes.muted}>
                        Atualizado: {formatDateTime(ticket.updatedAt)}
                      </Typography>
                      <Typography className={classes.lastMessage}>
                        {ticket.lastMessage || "Sem última mensagem"}
                      </Typography>
                      <div className={classes.cardActions}>
                        <div className={classes.quickActions}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<ErrorOutlineIcon />}
                            disabled={savingTicketId === ticket.id}
                            onClick={event =>
                              saveQuickStatus(event, ticket, "issue")
                            }
                          >
                            Pendência
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CheckCircleIcon />}
                            disabled={savingTicketId === ticket.id}
                            onClick={event =>
                              saveQuickStatus(event, ticket, "approved")
                            }
                          >
                            Aprovar
                          </Button>
                        </div>
                        <FormControl
                          className={classes.statusSelect}
                          variant="outlined"
                          size="small"
                          onClick={event => event.stopPropagation()}
                        >
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={draft.status}
                            onChange={event =>
                              handleDraftChange(
                                ticket.id,
                                "status",
                                event.target.value
                              )
                            }
                            label="Status"
                          >
                            {auditColumns.map(status => (
                              <MenuItem key={status.key} value={status.key}>
                                {status.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Observação"
                          value={draft.observation}
                          onClick={event => event.stopPropagation()}
                          onChange={event =>
                            handleDraftChange(
                              ticket.id,
                              "observation",
                              event.target.value
                            )
                          }
                          variant="outlined"
                          size="small"
                          multiline
                          minRows={2}
                        />
                        <Button
                          color="primary"
                          variant="contained"
                          size="small"
                          startIcon={<SaveIcon />}
                          disabled={savingTicketId === ticket.id}
                          onClick={event => {
                            event.stopPropagation();
                            saveAudit(ticket);
                          }}
                        >
                          Salvar
                        </Button>
                      </div>
                    </Paper>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      <Dialog
        open={conversationOpen}
        onClose={closeConversation}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">
            Conversa do ticket #{selectedTicket?.id}
          </Typography>
          <div className={classes.dialogActions}>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              startIcon={<OpenInNewIcon />}
              onClick={openTicketPage}
            >
              Abrir atendimento
            </Button>
            <IconButton size="small" onClick={closeConversation}>
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className={classes.ticketMeta}>
            <div className={classes.metaItem}>
              <Typography className={classes.muted}>Contato</Typography>
              <Typography>
                {selectedTicket?.contact?.name || "Contato"} |{" "}
                {selectedTicket?.contact?.number || "-"}
              </Typography>
            </div>
            <div className={classes.metaItem}>
              <Typography className={classes.muted}>Usuário</Typography>
              <Typography>{selectedTicket?.user?.name || "Sem usuário"}</Typography>
            </div>
            <div className={classes.metaItem}>
              <Typography className={classes.muted}>Departamento</Typography>
              <Typography>
                {selectedTicket?.queue?.name || "Sem departamento"}
              </Typography>
            </div>
            <div className={classes.metaItem}>
              <Typography className={classes.muted}>Etiqueta</Typography>
              <Typography>
                {(selectedTicket?.tags || []).map(tag => tag.name).join(", ") ||
                  selectedTicket?.tag?.name ||
                  "Sem etiqueta"}
              </Typography>
            </div>
            <div className={classes.metaItem}>
              <Typography className={classes.muted}>Instância</Typography>
              <Typography>{selectedTicket?.whatsapp?.name || "Sem instância"}</Typography>
            </div>
          </div>
          <div className={classes.messagesBox}>
            {messagesHasMore && (
              <Button
                fullWidth
                variant="outlined"
                size="small"
                className={classes.loadMore}
                disabled={messagesLoading}
                onClick={loadPreviousMessages}
              >
                Carregar mensagens anteriores
              </Button>
            )}
            {messagesLoading && messages.length === 0 && <CircularProgress />}
            {!messagesLoading && messages.length === 0 && (
              <div className={classes.empty}>Sem mensagens neste ticket</div>
            )}
            {messages.map(message => (
              <div
                key={message.id}
                className={`${classes.messageRow} ${
                  message.fromMe ? classes.messageRowFromMe : ""
                }`}
              >
                <div
                  className={`${classes.messageBubble} ${
                    message.fromMe ? classes.messageBubbleFromMe : ""
                  }`}
                >
                  <span className={classes.messageMeta}>
                    {message.fromMe
                      ? message.user?.name || "Atendente"
                      : message.contact?.name || "Contato"}{" "}
                    | {formatDateTime(message.createdAt)}
                  </span>
                  {renderMessageBody(message)}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketAudits;
